import { useEffect, useState } from "react";
import api, { errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { IndianRupee, LogOut, Phone, Sparkles, FileText, RefreshCw, MessagesSquare, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import UniversalLeadAdd from "@/components/app/UniversalLeadAdd";
import PipelineTimeline from "@/components/app/PipelineTimeline";
import RejectionHistory from "@/components/app/RejectionHistory";
import LocationCard from "@/components/app/LocationCard";
import AssignedLocationCard from "@/components/app/AssignedLocationCard";

function RefundRequestButton({ leadId, onDone }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const submit = async () => {
    if (!reason.trim()) return toast.error("Please provide a reason");
    try {
      await api.post(`/customer/refund-request/${leadId}`, { reason: reason.trim() });
      toast.success("Refund request submitted");
      setOpen(false); setReason(""); onDone && onDone();
    } catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`refund-req-${leadId}`} variant="secondary" className="bg-slate-800 hover:bg-slate-700 text-white rounded-full">
          <RefreshCw className="w-4 h-4 mr-2"/>Request Refund
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-slate-900 border-slate-800 text-white">
        <DialogHeader><DialogTitle>Request refund for {leadId}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-slate-400">You'll receive fees paid minus 18% GST (GST is paid to government and cannot be refunded).</div>
          <Label className="text-slate-400 text-xs uppercase tracking-widest">Reason</Label>
          <Textarea data-testid="refund-reason-input" value={reason} onChange={e=>setReason(e.target.value)} placeholder="Why do you want a refund?" className="bg-slate-950 border-slate-700"/>
          <Button data-testid="refund-submit" onClick={submit} className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full">Submit Refund Request</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function CustomerDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [leads, setLeads] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/customer/leads");
      setLeads(data);
    } catch (e) { toast.error(errorMessage(e)); }
  };

  useEffect(() => { load(); }, []);

  const pay = async (id) => {
    try {
      await api.post(`/customer/pay/${id}`);
      toast.success("Payment successful (MOCK)");
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  return (
    <div className="min-h-screen grain">
      <div className="border-b border-white/5 backdrop-blur-xl bg-slate-950/40 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500 flex items-center justify-center gold-glow"><Sparkles className="w-5 h-5 text-slate-900"/></div>
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-amber-500 font-bold">Customer</div>
              <div className="font-display text-xl text-white">Welcome, {user?.name}</div>
            </div>
          </div>
          <Button variant="ghost" onClick={()=>{logout(); nav("/");}} data-testid="logout-btn" className="text-slate-400 hover:text-red-400"><LogOut className="w-4 h-4 mr-2"/>Logout</Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-10 space-y-8">
        <UniversalLeadAdd/>

        {/* Customer self-share location — one card, applies to all their leads */}
        {leads.length > 0 && (
          <LocationCard
            mode="customer"
            location={leads[0]?.location}
            onSaved={(loc) => setLeads((prev) => prev.map(l => ({ ...l, location: loc })))}
          />
        )}

        {leads.length === 0 && (
          <div className="card-surface p-12 text-center">
            <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4"/>
            <p className="text-slate-400">No applications yet.</p>
          </div>
        )}
        {leads.map((lead) => {
          return (
            <motion.div key={lead.lead_id} initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} className="card-surface p-8" data-testid={`lead-${lead.lead_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4 border-b border-white/5 pb-6 mb-6">
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500">Reference ID</div>
                  <div className="font-mono-alt text-xl text-amber-500 mt-1">{lead.lead_id}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500">Loan Type</div>
                  <div className="text-white font-heading text-lg mt-1">{lead.loan_type}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500">Amount</div>
                  <div className="text-white font-heading text-lg mt-1 flex items-center"><IndianRupee className="w-4 h-4"/>{parseFloat(lead.amount).toLocaleString("en-IN")}</div>
                </div>
                {lead.bank_display && (
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500">Bank</div>
                    <div className="text-white font-heading text-lg mt-1">{lead.bank_display}</div>
                  </div>
                )}
              </div>

              {lead.rejections && lead.rejections.length > 0 && (
                <div className="mb-6"><RejectionHistory lead={lead}/></div>
              )}

              <PipelineTimeline lead={lead}/>

              {/* Assigned Branch + Partner Location (bidirectional view) */}
              <div className="mt-6">
                <AssignedLocationCard leadId={lead.lead_id}/>
              </div>

              {/* Your Loan Group — read-only view + join */}
              <div className="mt-6 rounded-2xl bg-slate-900/60 border border-[#25D366]/25 p-5" data-testid={`customer-wa-${lead.lead_id}`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-[#25D366]/15 border border-[#25D366]/40 flex items-center justify-center">
                    <MessagesSquare className="w-5 h-5 text-[#25D366]"/>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-widest text-[#25D366] font-bold">Your Loan Group</div>
                    <div className="text-sm text-white font-heading truncate">{lead.wa_group_name || "WhatsApp support group"}</div>
                  </div>
                </div>
                {lead.wa_group_invite_link ? (
                  <>
                    <a
                      href={lead.wa_group_invite_link}
                      target="_blank" rel="noreferrer"
                      data-testid={`customer-join-wa-${lead.lead_id}`}
                      className="w-full h-12 rounded-full bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold flex items-center justify-center gap-2">
                      <MessagesSquare className="w-4 h-4"/> Join WhatsApp Group for Updates
                    </a>
                    <div className="text-[10px] text-slate-500 mt-2 break-all font-mono-alt">{lead.wa_group_invite_link}</div>
                  </>
                ) : (
                  <div className="rounded-lg bg-orange-500/10 border border-orange-500/30 p-3 flex items-center gap-2 text-orange-300 text-xs" data-testid={`customer-wa-waiting-${lead.lead_id}`}>
                    <Clock className="w-4 h-4 shrink-0"/> Your group will be created soon by our team.
                  </div>
                )}
              </div>

              {lead.status === "ok" && lead.payment_status === "paid" && !lead.disbursed && (
                <div className="mt-4">
                  <RefundRequestButton leadId={lead.lead_id} onDone={load}/>
                </div>
              )}

              {lead.status === "ok" && lead.payment_status !== "paid" && (
                <div className="mt-6 flex flex-wrap items-center justify-between gap-4 bg-amber-500/10 border border-amber-500/30 rounded-xl p-5">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-amber-400 font-bold">Consulting fees due</div>
                    <div className="font-display text-2xl text-white mt-1 flex items-center"><IndianRupee className="w-5 h-5"/>{(lead.fees_amount || 0).toLocaleString("en-IN")}</div>
                  </div>
                  <Button onClick={()=>pay(lead.lead_id)} data-testid={`pay-${lead.lead_id}`} className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full">
                    Pay Now (Mock)
                  </Button>
                </div>
              )}

              <div className="mt-6 pt-6 border-t border-white/5 flex items-center gap-4 text-sm">
                <a href="https://wa.me/917010456250?text=Hi%20Sakthi%20Loan%20Consultancy%2C%20I%20need%20a%20loan" target="_blank" rel="noreferrer" className="text-amber-500 hover:text-amber-400 flex items-center gap-2"><Phone className="w-4 h-4"/>WhatsApp Support</a>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
